import type { ChannelFilterEntity } from '../../domain/entities/ChannelFilterEntity'
import { ChannelFilterModel } from '../models/ChannelFilterModel'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import { ChannelMiniModel } from '../models/ChannelMiniModel'
import { ChannelModel } from '../models/ChannelModel'
import type { CreateUpdateChannelEntity } from '../../domain/entities/CreateUpdateChannel'
import { CreateUpdateChannelModel } from '../models/CreateUpdateChannelModel'
import type { ChannelEntity } from '../../domain/entities/ChannelEntity'
import {
  ChannelsService,
  type ChannelRequestAdmin,
  type PostChannelRequestAdmin,
  type PutChannelRequestAdmin
} from '@/backend/market'
import type { ChannelRepository } from '../../domain/repositories/ChannelRepository'
import type { ChannelLayoutEntity } from '../../domain/entities/ChannelLayoutEntity'
import { ChannelLayoutModel } from '../models/ChannelLayoutModel'
import type { ChannelMiniEntity } from '../../domain/entities/ChannelMiniEntity'
import { ChannelOrderRegionModel } from '../models/ChannelOrderRegionModel'
import type { ChannelOrderRegionEntity } from '../../domain/entities/ChannelOrderRegionEntity'
import { ChannelActiveRegionModel } from '../models/ChannelActiveRegionModel'
import type { ChannelActiveRegionEntity } from '../../domain/entities/ChannelActiveRegionEntity'

export class ChannelRepositoryImpl implements ChannelRepository {
  async getChannels(
    filterData: ChannelFilterEntity
  ): Promise<PaginateEntity<ChannelMiniModel[]> | { error: true }> {
    const channelFilterModel = new ChannelFilterModel(filterData)
    try {
      const response = await ChannelsService.getChannelsAdmin(channelFilterModel)
      const channelMiniModel = response.items?.map((item) => new ChannelMiniModel(item))
      const result = new PaginateEntity<ChannelMiniModel[]>(response, channelMiniModel)

      return result
    } catch {
      return { error: true }
    }
  }
  async getChannel(id: number): Promise<ChannelEntity | { error: true }> {
    try {
      const response = await ChannelsService.getChannelAdmin({
        id
      })
      const channelModel = new ChannelModel(response)
      return channelModel
    } catch {
      return { error: true }
    }
  }
  async createChannel(
    channelData: CreateUpdateChannelEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateChannelModel = new CreateUpdateChannelModel(channelData)
    try {
      await ChannelsService.postChannelAdmin({
        requestBody: createUpdateChannelModel as PostChannelRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateChannel(
    id: number,
    channelData: CreateUpdateChannelEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateChannelModel = new CreateUpdateChannelModel(channelData)
    try {
      await ChannelsService.putChannelAdmin({
        id,
        requestBody: createUpdateChannelModel as PutChannelRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async deleteChannel(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await ChannelsService.deleteChannelAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async getChannelLayout(regionId: number): Promise<ChannelMiniEntity[] | { error: true }> {
    try {
      const response = await ChannelsService.getChannelLayoutsAdmin({
        regionId
      })

      const channelLayoutModels = response.channelLayouts?.map(
        (layout) => new ChannelMiniModel(layout)
      )
      return channelLayoutModels ?? []
    } catch {
      return { error: true }
    }
  }
  async updateChannelLayout(
    regionId: number,
    layouts: ChannelLayoutEntity[]
  ): Promise<{ success: true } | { error: true }> {
    const putChannelLayoutModels = layouts.map((layout) => new ChannelLayoutModel(layout))
    try {
      await ChannelsService.putChannelLayoutAdmin({
        requestBody: {
          regionId,
          layouts: putChannelLayoutModels as unknown as ChannelRequestAdmin[]
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async getActiveChannels(): Promise<ChannelMiniEntity[] | { error: true }> {
    try {
      const response = await ChannelsService.getAllChannelsAdmin()
      const channelMiniModel = response.channels?.map((item) => new ChannelMiniModel(item))
      return channelMiniModel ?? []
    } catch {
      return { error: true }
    }
  }

  async getChannelRegions(
    channelId: number
  ): Promise<ChannelActiveRegionEntity[] | { error: true }> {
    try {
      const response = await ChannelsService.getChannelRegionsAdmin({ id: channelId })
      const regionMiniModels = response.regions?.map(
        (item) => new ChannelActiveRegionModel(item as unknown as ChannelActiveRegionModel)
      )
      return regionMiniModels ?? []
    } catch {
      return { error: true }
    }
  }

  async updateChannelRegion(
    channelId: number,
    channelData: ChannelActiveRegionEntity[]
  ): Promise<{ success: true } | { error: true }> {
    const newData = channelData
      .filter((item) => item.region?.id)
      .map((item) => ({
        regionId: item.region?.id,
        coverImages: item.coverImages?.map((coverImage) => ({
          coverImage: coverImage.coverImage,
          serviceCoverImage: coverImage.serviceCoverImage,
          language: coverImage.language
        }))
      }))
    try {
      await ChannelsService.putChannelRegionsAdmin({
        id: channelId,
        requestBody: {
          activeRegions: newData
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async getChannelOrders(regionId: number): Promise<ChannelOrderRegionEntity[] | { error: true }> {
    try {
      const response = await ChannelsService.getChannelOrderRegionsAdmin({
        regionId
      })
      const channelOrderRegionModels = response.channelOrderRegions?.map(
        (item) => new ChannelOrderRegionModel(item)
      )
      return channelOrderRegionModels ?? []
    } catch {
      return { error: true }
    }
  }
  async updateChannelOrder(
    regionId: number,
    channelOrderRegions: { channelId: number; order: number }[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await ChannelsService.putChannelOrderRegionsAdmin({
        regionId,
        requestBody: {
          orderRegions: channelOrderRegions
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
}
